import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Layout
import Layout from './components/Layout';

// Pages
import Dashboard from './pages/Dashboard';
import Flats from './pages/Flats';
import Owners from './pages/Owners';
import Reports from './pages/Reports';
import Accounts from './pages/Accounts';
import AccountDetails from './pages/AccountDetails';
import Transactions from './pages/Transactions';
import Statements from './pages/Statements';
import Settings from './pages/Settings';
import Users from './pages/Users';
import Profile from './pages/Profile';  // Add this import
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Welcome from './pages/Welcome';

// Components
import ConfigError from './components/ConfigError';

// Supabase check
import { isSupabaseConfigured, supabase, getCurrentProfileSafe } from './lib/supabase';

const queryClient = new QueryClient();

const RequireAdministrator: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let aborted = false;

    const verify = async () => {
      try {
        const profile = await getCurrentProfileSafe();
        if (!aborted) {
          setAllowed(Boolean(profile?.role === 'administrator' && profile?.is_active !== false));
        }
      } catch (error) {
        if (!aborted) {
          setAllowed(false);
        }
      } finally {
        if (!aborted) {
          setChecking(false);
        }
      }
    };

    verify();

    return () => {
      aborted = true;
    };
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Checking administrator access...</p>
        </div>
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const ProtectedRoutes: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }: any) => {
      setSession(data.session);
      setCheckingSession(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event: string, nextSession: any) => {
      setSession(nextSession);
      setCheckingSession(false);
    });

    return () => data.subscription.unsubscribe();
  }, []);

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Checking session...</p>
        </div>
      </div>
    );
  }

  if (!session) return <Welcome />;

  return <Layout />;
};

function App() {
  const [isConfigured, setIsConfigured] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkConfig = () => {
      const configured = isSupabaseConfigured();
      setIsConfigured(configured);
      setChecking(false);
      
      if (!configured) {
        console.warn('⚠️ Supabase is not configured. Please set up your .env file.');
      } else {
        console.log('✅ Supabase is configured');
      }
    };

    checkConfig();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Checking configuration...</p>
        </div>
      </div>
    );
  }

  if (!isConfigured) {
    return (
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ConfigError onRetry={() => window.location.reload()} />
      </Router>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#22c55e',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoutes />}>
            <Route index element={<Dashboard />} />
            <Route path="flats" element={<Flats />} />
            <Route path="owners" element={<Owners />} />
            <Route path="accounts" element={<Accounts />} />
            <Route path="accounts/:id" element={<AccountDetails />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="statements" element={<Statements />} />
            <Route path="reports" element={<Reports />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<RequireAdministrator><Settings /></RequireAdministrator>} />
            <Route path="users" element={<RequireAdministrator><Users /></RequireAdministrator>} />
          </Route>
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
